import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ApiError } from './api';
import { AuthProvider, useAuth, usePermissions } from './auth';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/ui';
import { AgentsPage } from './pages/Agents';
import { ApiKeysPage } from './pages/ApiKeys';
import { AuditPage } from './pages/Audit';
import { ForcedPasswordChange } from './pages/ChangePassword';
import { GroupsPage } from './pages/Groups';
import { JobsPage } from './pages/Jobs';
import { LoginPage } from './pages/Login';
import { UploadPage } from './pages/Upload';
import { UsagePage } from './pages/Usage';
import { UsersPage } from './pages/Users';
import { RealtimeProvider } from './realtime';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // No reintentar errores de permisos o validación.
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

function RequireAdmin({ children, superOnly }: { children: ReactNode; superOnly?: boolean }) {
  const perms = usePermissions();
  if (superOnly ? !perms.isSuper : !perms.isAdmin) return <Navigate to="/jobs" replace />;
  return <>{children}</>;
}

function App() {
  const { me, loading } = useAuth();

  if (loading) {
    return (
      <div className="center-screen">
        <Loader2 className="spin" />
      </div>
    );
  }
  if (!me) return <LoginPage />;
  if (me.user.mustChangePassword) return <ForcedPasswordChange />;

  return (
    <RealtimeProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/groups" element={<RequireAdmin superOnly><GroupsPage /></RequireAdmin>} />
          <Route path="/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
          <Route path="/api-keys" element={<RequireAdmin><ApiKeysPage /></RequireAdmin>} />
          <Route path="/agents" element={<RequireAdmin><AgentsPage /></RequireAdmin>} />
          <Route path="/usage" element={<RequireAdmin><UsagePage /></RequireAdmin>} />
          <Route path="/audit" element={<RequireAdmin><AuditPage /></RequireAdmin>} />
          <Route path="*" element={<Navigate to="/jobs" replace />} />
        </Route>
      </Routes>
    </RealtimeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
